import { Button, Modal, Textarea } from "@mantine/core";
import type React from "react";
import { useNavigate } from "react-router-dom";
import type { DeliverablesData, TrainerRequest } from "../../../types/portal";
import EmptyState from "../../shared/EmptyState";
import LoadingState from "../../shared/LoadingState";

interface TrainerHistoryTabProps {
  sessionHistory: TrainerRequest[];
  loading: boolean;
  onRefresh: () => void;
  selectedDeliverablesRequest: TrainerRequest | null;
  onOpenDeliverables: (request: TrainerRequest) => void;
  onCloseDeliverables: () => void;
  deliverablesData: Record<string, DeliverablesData>;
  savingDeliverables: Record<string, boolean>;
  onDeliverablesInputChange: (requestId: string, field: string, value: string | string[]) => void;
  onChecklistItemChange: (requestId: string, index: number, value: string) => void;
  onAddChecklistItem: (requestId: string) => void;
  onRemoveChecklistItem: (requestId: string, index: number) => void;
  onSaveDeliverables: (requestId: string, request: TrainerRequest) => void;
}

const TrainerHistoryTab: React.FC<TrainerHistoryTabProps> = ({
  sessionHistory,
  loading,
  onRefresh,
  selectedDeliverablesRequest,
  onOpenDeliverables,
  onCloseDeliverables,
  deliverablesData,
  savingDeliverables,
  onDeliverablesInputChange,
  onChecklistItemChange,
  onAddChecklistItem,
  onRemoveChecklistItem,
  onSaveDeliverables,
}) => {
  const navigate = useNavigate();
  return (
  <div>
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-[30px] gap-3">
      <h2 className="text-xl sm:text-2xl text-foreground m-0 mb-0 sm:mb-5">Session History</h2>
      <Button
        onClick={onRefresh}
        disabled={loading}
        className={`!px-4 !py-2 !bg-muted !text-muted-foreground !border !border-border !rounded-md !text-[0.9rem] !font-medium hover:!bg-border ${loading ? "!bg-border !cursor-wait !opacity-70" : ""}`}
      >
        Refresh
      </Button>
    </div>
    <div className="flex flex-col gap-5">
      {loading ? (
        <LoadingState message="Loading session history..." />
      ) : sessionHistory.length === 0 ? (
        <EmptyState
          title="No Session History"
          description="Your completed training sessions will appear here."
        />
      ) : (
        sessionHistory.map((request: TrainerRequest, index: number) => {
          const clientName = request.client_name || "Unknown Client";
          const sessionDate = request.accepted_at || request.requested_at;
          const formattedDate = sessionDate ? new Date(sessionDate).toLocaleDateString() : "N/A";
          const formattedTime = sessionDate ? new Date(sessionDate).toLocaleTimeString() : "N/A";

          return (
            <div
              key={request.id || index}
              className="bg-muted rounded-lg px-3 sm:px-5 py-4 border border-border mb-3 transition-all duration-300 shadow-md"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 w-full">
                <div className="min-w-[150px]">
                  <div className="text-base font-semibold text-foreground mb-1">
                    Session #{request.id?.slice(-8) || index}
                  </div>
                  <div className="text-[0.85rem] text-muted-foreground">{formattedDate}</div>
                </div>
                <div className="flex-1 flex items-center flex-wrap text-[0.9rem]">
                  <span className="text-[0.9rem] text-muted-foreground font-medium mr-1">Client: </span>
                  <span className="text-[0.9rem] text-foreground font-medium">{clientName}</span>
                  <span className="text-muted-foreground mx-2"> | </span>
                  <span className="text-[0.9rem] text-muted-foreground font-medium mr-1">
                    Duration:{" "}
                  </span>
                  <span className="text-[0.9rem] text-foreground font-medium">
                    {request.duration_minutes || 0} minutes
                  </span>
                  <span className="text-muted-foreground mx-2"> | </span>
                  <span className="text-[0.9rem] text-muted-foreground font-medium mr-1">Time: </span>
                  <span className="text-[0.9rem] text-foreground font-medium">{formattedTime}</span>
                  {request.pet_name && (
                    <>
                      <span className="text-muted-foreground mx-2"> | </span>
                      <span className="text-[0.9rem] text-muted-foreground font-medium mr-1">Pet: </span>
                      <span className="text-[0.9rem] text-foreground font-medium">
                        {request.pet_name}
                      </span>
                    </>
                  )}
                  {request.total_price !== undefined && request.total_price !== null && (
                    <>
                      <span className="text-muted-foreground mx-2"> | </span>
                      <span className="text-[0.9rem] text-muted-foreground font-medium mr-1">
                        Price:{" "}
                      </span>
                      <span className="text-[0.9rem] text-foreground font-medium">
                        ${Number(request.total_price || 0).toFixed(2)}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex flex-col gap-2.5 items-start sm:items-end">
                  <span className="inline-flex items-center bg-success/15 text-success px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                    Completed
                  </span>
                  <div className="flex flex-col gap-2.5 w-[180px]">
                    <Button fullWidth radius="md" onClick={() => onOpenDeliverables(request)}>
                      {deliverablesData[request.id] ? "Edit Deliverables" : "Add Deliverables"}
                    </Button>
                    <Button
                      fullWidth
                      radius="md"
                      variant="default"
                      onClick={() => navigate(`/post-session?request_id=${request.id}`)}
                    >
                      {request.session_summary ? "Edit Summary" : "Add Summary"}
                    </Button>
                  </div>
                </div>
              </div>
              {request.notes ? (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="text-[0.85rem] text-muted-foreground font-medium mb-1">
                    Your session notes
                  </div>
                  <div className="text-[0.9rem] text-foreground whitespace-pre-wrap">
                    {String(request.notes)}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })
      )}
    </div>

    <Modal
      opened={!!selectedDeliverablesRequest}
      onClose={onCloseDeliverables}
      title="Session Deliverables"
      size="lg"
      classNames={{
        content: "!bg-card !border !border-border",
        header: "!bg-card !border-b !border-border",
        title: "!text-2xl !font-semibold !text-foreground",
        close: "!text-muted-foreground hover:!text-foreground",
        body: "!bg-card !p-6",
      }}
    >
      {selectedDeliverablesRequest && (
        <>
          <div className="mb-5 p-3 bg-muted border border-border rounded-md text-[0.85rem] text-muted-foreground">
            Deliverables are private to you — they are <strong>not visible to the client</strong>.
            Use them to track goals and notes for yourself. To share something with the client, edit
            the Session Summary instead.
          </div>
          <div className="mb-5">
            <label className="block text-[0.9rem] font-medium text-muted-foreground mb-2">
              Summary <span className="text-destructive text-[0.9rem]">*</span>
            </label>
            <Textarea
              value={deliverablesData[selectedDeliverablesRequest.id]?.summary || ""}
              onChange={(e) =>
                onDeliverablesInputChange(selectedDeliverablesRequest.id, "summary", e.target.value)
              }
              placeholder="Enter a short summary of the main points covered in this session..."
              minRows={6}
              autosize
              classNames={{
                input:
                  "!bg-input !text-foreground !border-border !rounded-lg !max-h-[300px] !overflow-y-auto",
              }}
            />
          </div>

          <div className="mb-5">
            <label className="block text-[0.9rem] font-medium text-muted-foreground mb-2">
              Checklist Items
            </label>
            {(deliverablesData[selectedDeliverablesRequest.id]?.checklist_items || []).map(
              (item: string, itemIndex: number) => (
                <div key={itemIndex} className="flex gap-2 mb-2.5 items-center">
                  <Textarea
                    value={item}
                    onChange={(e) =>
                      onChecklistItemChange(
                        selectedDeliverablesRequest.id,
                        itemIndex,
                        e.target.value,
                      )
                    }
                    placeholder={`Checklist item ${itemIndex + 1}`}
                    minRows={Math.max(2, Math.ceil(item.length / 50))}
                    autosize
                    className="flex-1"
                    classNames={{
                      input:
                        "!bg-input !text-foreground !border-border !rounded-md !min-h-[40px] !overflow-y-auto",
                    }}
                  />
                  <Button
                    onClick={() => onRemoveChecklistItem(selectedDeliverablesRequest.id, itemIndex)}
                    color="red"
                    radius="md"
                    className="!px-4 !py-2.5 !text-xl !font-bold !leading-none"
                  >
                    &#x00D7;
                  </Button>
                </div>
              ),
            )}
            <Button
              onClick={() => onAddChecklistItem(selectedDeliverablesRequest.id)}
              variant="default"
              radius="md"
            >
              + Add Checklist Item
            </Button>
          </div>

          <div className="pt-5 border-t border-border flex justify-end gap-3">
            <Button onClick={onCloseDeliverables} variant="default" radius="md">
              Cancel
            </Button>
            <Button
              onClick={() => {
                onSaveDeliverables(selectedDeliverablesRequest.id, selectedDeliverablesRequest);
                onCloseDeliverables();
              }}
              disabled={
                savingDeliverables[selectedDeliverablesRequest.id] ||
                !deliverablesData[selectedDeliverablesRequest.id]?.summary
              }
              color="green"
              radius="md"
            >
              {savingDeliverables[selectedDeliverablesRequest.id]
                ? "Saving..."
                : "Save Deliverables"}
            </Button>
          </div>
        </>
      )}
    </Modal>
  </div>
  );
};

export default TrainerHistoryTab;
